import { useMemo } from 'react';
import { useQueries } from 'react-query';
import { ScheduleApi, ScheduleTypes } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersConstants } from '@frontend/api-service-providers';
import { queryKeys } from '../../query-keys';
import { useGetOperatoryV3Data } from './scheduler-v3';

const { getOperatories } = ScheduleApi;
const { ONE_MINUTE_IN_MILLISECONDS } = ServiceProvidersConstants;

export const useGetOperatoryList = (locationIds: string[]) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const queryResult = useQueries(
    locationIds.map((locationId) => ({
      queryKey: queryKeys.listOperatories([locationId]),
      queryFn: async () => await getOperatories(locationId),
      retry: 1,
      cacheTime: 5 * ONE_MINUTE_IN_MILLISECONDS, // 5 mins
      staleTime: 5 * ONE_MINUTE_IN_MILLISECONDS, // 5 mins
      enabled: !!locationId && !isScheduleV3FlagEnabled,
      select: (data: ScheduleTypes.OperatoryTypeResponse) => {
        if (data?.data?.operatory === undefined) return [];
        return data.data.operatory
          .filter((operatory) => operatory.value !== '')
          .map((operatory) => ({
            ...operatory,
            locationId: locationId,
          }));
      },
    }))
  );

  const operatories = useMemo(
    () =>
      queryResult
        .map((result) => {
          if (result.isSuccess) {
            return result.data;
          }
          return [];
        })
        .flat()
        .filter(Boolean),
    [queryResult]
  );

  const isLoading = queryResult.some((result) => result.isLoading);
  const isError = queryResult.some((result) => result.isError);

  const {
    data: operatoriesDataV3,
    isLoading: isLoadingOperatoriesData,
    isError: isErrorOperatoriesData,
  } = useGetOperatoryV3Data({
    selectedLocationId: locationIds[0],
    selectedLocationIds: locationIds,
    isEnabled: isScheduleV3FlagEnabled,
  });

  if (isScheduleV3FlagEnabled) {
    const operatoriesDataV2 = (operatoriesDataV3?.operatories ?? [])
      .filter((operatory) => operatory.externalName !== '' && !operatory.hidden && operatory.details?.act)
      .map<ScheduleTypes.OperatoryType>((operatory) => {
        return {
          display_name: operatory.externalName ?? '',
          key: operatory.id ?? '',
          value: operatory.id ?? '',
          locationId: operatory.locationId,
        };
      });

    return { operatories: operatoriesDataV2, isLoading: isLoadingOperatoriesData, isError: isErrorOperatoriesData };
  }

  return {
    operatories,
    isLoading,
    isError,
  };
};
